import { Injectable, OnDestroy, inject } from '@angular/core';
import { PluginListenerHandle } from '@capacitor/core';
import { FacingMode } from '@avatar/services/webrtc-base.service';
import CallKit, {
  VideoCallBackgroundCameraDebugEvent,
  VideoCallBackgroundCameraFrame,
  VideoCallBackgroundCameraOptions,
} from '@plugins/android_callkit/callkit.plugin';
import { AppStateChangeService } from '@services/app-state-change.service';
import { PlatformService } from '@services/platform.service';
import { SubscriptionsBag } from '@utils/subscriptions-bag';

interface IosVideoCallBackgroundCameraCallbacks {
  releaseForegroundVideoTrack(): void;
  restoreForegroundVideoTrack(): Promise<MediaStreamTrack | null>;
  replaceSenderVideoTrack(videoTrack: MediaStreamTrack): Promise<void>;
}

@Injectable({
  providedIn: 'root'
})
export class IosVideoCallBackgroundCameraService implements OnDestroy {
  private readonly appStateChangeService: AppStateChangeService = inject(AppStateChangeService);
  private readonly platformService: PlatformService = inject(PlatformService);

  private readonly sb: SubscriptionsBag = new SubscriptionsBag();
  private readonly documentVisibilityChangeHandler: () => void = (): void => {
    if (!this.orderId) return;
    this.queueSync(this.shouldUseNativeFrames() ? 0 : 250);
  };

  private orderId: number | null = null;
  private currentFacingMode: FacingMode = 'user';
  private outgoingVideoTrack: MediaStreamTrack | null = null;
  private frameCanvasElement: HTMLCanvasElement | null = null;
  private frameCanvasContext: CanvasRenderingContext2D | null = null;
  private frameHostElement: HTMLDivElement | null = null;
  private foregroundVideoElement: HTMLVideoElement | null = null;
  private foregroundVideoStream: MediaStream | null = null;
  private foregroundFrameRequestId: number | null = null;
  private nativeFrameListener: PluginListenerHandle | null = null;
  private nativeDebugListener: PluginListenerHandle | null = null;
  private nativeFrameImage: HTMLImageElement | null = null;
  private pendingNativeFrameDataUrl: string | null = null;
  private isNativeFrameImageLoading: boolean = false;
  private queuedSyncTimeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
  private isUsingNativeFrames: boolean = false;
  private callbacks: IosVideoCallBackgroundCameraCallbacks | null = null;
  private readonly foregroundFrameReadyTimeoutMs: number = 2500;

  public constructor() {
    if (!this.platformService.isIosApp) return;

    this.subscribeToAppStateChanges();
    this.subscribeToDocumentVisibilityChanges();
  }

  public ngOnDestroy(): void {
    this.unsubscribeFromDocumentVisibilityChanges();
    this.clearQueuedSync();
    void this.deactivate();
    this.sb.unsubscribeAll();
  }

  public async prepareOutgoingVideoTrack(options: {
    orderId: number;
    sourceVideoTrack: MediaStreamTrack;
    facingMode: FacingMode;
    callbacks: IosVideoCallBackgroundCameraCallbacks;
  }): Promise<MediaStreamTrack | null> {
    if (!this.platformService.isIosApp) return null;

    await this.logNativeCheckpoint('ios_background_camera_prepare_enter', {
      orderId: options.orderId,
      facingMode: options.facingMode,
      sourceTrackReadyState: options.sourceVideoTrack.readyState,
    });

    this.orderId = options.orderId;
    this.currentFacingMode = options.facingMode;
    this.callbacks = options.callbacks;

    await this.ensureNativeFrameListener();
    await this.ensureNativeDebugListener();
    this.ensureFrameCanvas();

    if (!this.outgoingVideoTrack || !this.frameCanvasContext) {
      await this.logNativeCheckpoint('ios_background_camera_prepare_skipped', {
        hasOutgoingVideoTrack: !!this.outgoingVideoTrack,
        hasFrameCanvasContext: !!this.frameCanvasContext,
      });
      this.orderId = null;
      this.callbacks = null;
      this.clearFrameCanvas();
      return null;
    }

    try {
      const shouldUseNativeFrames: boolean = this.shouldUseNativeFrames();

      await this.setForegroundVideoTrack(options.sourceVideoTrack);
      if (!shouldUseNativeFrames) {
        await this.ensureInitialForegroundFrameDrawn();
      }
      this.startForegroundFramePump();
      this.queueSync(shouldUseNativeFrames ? 0 : 250);
    } catch (error) {
      await this.deactivate();
      throw error;
    }

    return this.shouldUseNativeFrames() ? this.outgoingVideoTrack : null;
  }

  public updateFacingMode(facingMode: FacingMode): void {
    this.currentFacingMode = facingMode;

    if (!this.platformService.isIosApp || !this.orderId) return;

    void this.syncNativeCameraFacingMode();
  }

  public async deactivate(): Promise<void> {
    if (!this.platformService.isIosApp) return;

    this.clearQueuedSync();
    this.stopForegroundFramePump();
    this.isUsingNativeFrames = false;
    this.orderId = null;
    this.callbacks = null;

    if (this.nativeFrameListener) {
      const listener: PluginListenerHandle = this.nativeFrameListener;
      this.nativeFrameListener = null;
      await listener.remove();
    }

    if (this.nativeDebugListener) {
      const listener: PluginListenerHandle = this.nativeDebugListener;
      this.nativeDebugListener = null;
      await listener.remove();
    }

    await this.deactivateNativeCamera();
    this.clearFrameCanvas();
  }

  private subscribeToAppStateChanges(): void {
    this.sb.sub = this.appStateChangeService.appStateChange$.subscribe((isActive: boolean): void => {
      if (!this.orderId) return;
      this.queueSync(isActive && !this.isDocumentHidden() ? 250 : 0);
    });
  }

  private subscribeToDocumentVisibilityChanges(): void {
    if (this.platformService.isSSR) return;
    if (typeof document === 'undefined') return;
    if (typeof window === 'undefined') return;

    document.addEventListener('visibilitychange', this.documentVisibilityChangeHandler);
    window.addEventListener('blur', this.documentVisibilityChangeHandler);
    window.addEventListener('focus', this.documentVisibilityChangeHandler);
    window.addEventListener('pagehide', this.documentVisibilityChangeHandler);
    window.addEventListener('pageshow', this.documentVisibilityChangeHandler);
  }

  private unsubscribeFromDocumentVisibilityChanges(): void {
    if (this.platformService.isSSR) return;
    if (typeof document === 'undefined') return;
    if (typeof window === 'undefined') return;

    document.removeEventListener('visibilitychange', this.documentVisibilityChangeHandler);
    window.removeEventListener('blur', this.documentVisibilityChangeHandler);
    window.removeEventListener('focus', this.documentVisibilityChangeHandler);
    window.removeEventListener('pagehide', this.documentVisibilityChangeHandler);
    window.removeEventListener('pageshow', this.documentVisibilityChangeHandler);
  }

  private async ensureNativeFrameListener(): Promise<void> {
    if (this.nativeFrameListener) return;

    this.nativeFrameListener = await CallKit.addListener(
      'videoCallBackgroundCameraFrame',
      (frame: VideoCallBackgroundCameraFrame): void => {
        if (!this.isUsingNativeFrames || !frame.dataUrl) return;
        this.queueNativeFrame(frame.dataUrl);
      },
    );
  }

  private async ensureNativeDebugListener(): Promise<void> {
    if (this.nativeDebugListener) return;

    this.nativeDebugListener = await CallKit.addListener(
      'videoCallBackgroundCameraDebug',
      (event: VideoCallBackgroundCameraDebugEvent): void => {
        console.warn('iOS video call background camera debug', event);
      },
    );
  }

  private ensureFrameCanvas(): void {
    if (this.platformService.isSSR) return;
    if (typeof document === 'undefined') return;

    if (!this.frameHostElement) {
      const hostElement: HTMLDivElement = document.createElement('div');
      hostElement.style.position = 'fixed';
      hostElement.style.right = '0';
      hostElement.style.bottom = '0';
      hostElement.style.width = '1px';
      hostElement.style.height = '1px';
      hostElement.style.opacity = '0.01';
      hostElement.style.pointerEvents = 'none';
      hostElement.style.overflow = 'hidden';
      hostElement.style.zIndex = '-1';
      document.body.appendChild(hostElement);
      this.frameHostElement = hostElement;
    }

    if (!this.frameCanvasElement) {
      const hostElement: HTMLDivElement | null = this.frameHostElement;
      if (!hostElement) return;

      const canvasElement: HTMLCanvasElement = document.createElement('canvas');
      canvasElement.width = 480;
      canvasElement.height = 640;
      this.frameCanvasElement = canvasElement;
      hostElement.appendChild(canvasElement);
    }

    const canvasElement: HTMLCanvasElement | null = this.frameCanvasElement;
    if (!canvasElement) return;

    if (!this.frameCanvasContext) {
      this.frameCanvasContext = canvasElement.getContext('2d');
    }

    if (!this.outgoingVideoTrack && typeof canvasElement.captureStream === 'function') {
      const canvasStream: MediaStream = canvasElement.captureStream(12);
      this.outgoingVideoTrack = canvasStream.getVideoTracks()[0] ?? null;
    }
  }

  private async setForegroundVideoTrack(videoTrack: MediaStreamTrack): Promise<void> {
    if (this.platformService.isSSR) return;
    if (typeof document === 'undefined') return;

    this.ensureFrameCanvas();

    if (!this.foregroundVideoElement) {
      const videoElement: HTMLVideoElement = document.createElement('video');
      videoElement.autoplay = true;
      videoElement.muted = true;
      videoElement.playsInline = true;
      videoElement.setAttribute('playsinline', '');
      videoElement.setAttribute('webkit-playsinline', '');
      videoElement.style.width = '1px';
      videoElement.style.height = '1px';
      this.foregroundVideoElement = videoElement;
      this.frameHostElement?.appendChild(videoElement);
    }

    this.foregroundVideoStream = new MediaStream([videoTrack]);
    this.foregroundVideoElement.srcObject = this.foregroundVideoStream;

    try {
      await this.foregroundVideoElement.play();
    } catch (error) {
      console.warn('Failed to play iOS background camera foreground bridge video', error);
    }
  }

  private clearFrameCanvas(): void {
    if (this.outgoingVideoTrack) {
      this.outgoingVideoTrack.stop();
      this.outgoingVideoTrack = null;
    }

    if (this.foregroundVideoElement) {
      this.foregroundVideoElement.pause();
      this.foregroundVideoElement.srcObject = null;
      this.foregroundVideoElement.remove();
      this.foregroundVideoElement = null;
    }

    if (this.frameCanvasElement) {
      this.frameCanvasElement.remove();
      this.frameCanvasElement = null;
    }

    if (this.frameHostElement) {
      this.frameHostElement.remove();
      this.frameHostElement = null;
    }

    this.foregroundVideoStream = null;
    this.frameCanvasContext = null;
    this.nativeFrameImage = null;
    this.pendingNativeFrameDataUrl = null;
    this.isNativeFrameImageLoading = false;
  }

  private startForegroundFramePump(): void {
    if (!this.foregroundVideoElement || this.isUsingNativeFrames) return;
    if (this.foregroundFrameRequestId !== null) return;
    if (typeof window === 'undefined') return;

    const pump = (): void => {
      this.foregroundFrameRequestId = null;

      if (!this.orderId || this.isUsingNativeFrames) return;

      this.drawCurrentForegroundFrame();
      this.foregroundFrameRequestId = window.requestAnimationFrame(pump);
    };

    this.foregroundFrameRequestId = window.requestAnimationFrame(pump);
  }

  private stopForegroundFramePump(): void {
    const requestId: number | null = this.foregroundFrameRequestId;
    this.foregroundFrameRequestId = null;

    if (requestId === null) return;
    if (typeof window === 'undefined') return;

    window.cancelAnimationFrame(requestId);
  }

  private ensureInitialForegroundFrameDrawn(): Promise<void> {
    if (this.drawCurrentForegroundFrame()) return Promise.resolve();
    if (typeof window === 'undefined') return Promise.resolve();

    return new Promise<void>((resolve, reject): void => {
      let settled: boolean = false;
      let frameRequestId: number | null = null;
      let timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;

      const cleanup = (): void => {
        if (frameRequestId !== null) {
          window.cancelAnimationFrame(frameRequestId);
          frameRequestId = null;
        }
        if (timeoutId) {
          globalThis.clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      const finish = (error: Error | null): void => {
        if (settled) return;
        settled = true;
        cleanup();

        if (error) {
          reject(error);
          return;
        }

        resolve();
      };

      const tick = (): void => {
        frameRequestId = null;
        if (this.drawCurrentForegroundFrame()) {
          finish(null);
          return;
        }

        frameRequestId = window.requestAnimationFrame(tick);
      };

      timeoutId = globalThis.setTimeout((): void => {
        finish(this.createForegroundFrameError());
      }, this.foregroundFrameReadyTimeoutMs);
      frameRequestId = window.requestAnimationFrame(tick);
    });
  }

  private drawCurrentForegroundFrame(): boolean {
    const videoElement: HTMLVideoElement | null = this.foregroundVideoElement;
    if (!videoElement || videoElement.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return false;

    const width: number = videoElement.videoWidth || 480;
    const height: number = videoElement.videoHeight || 640;
    this.drawFrame(videoElement, width, height);
    return true;
  }

  private createForegroundFrameError(): Error {
    const error: Error = new Error('iOS background camera bridge did not produce first frame.');
    error.name = 'AbortError';
    return error;
  }

  private queueNativeFrame(dataUrl: string): void {
    this.pendingNativeFrameDataUrl = dataUrl;

    if (this.isNativeFrameImageLoading) {
      return;
    }

    this.loadPendingNativeFrame();
  }

  private loadPendingNativeFrame(): void {
    const dataUrl: string | null = this.pendingNativeFrameDataUrl;
    if (!dataUrl) return;

    this.pendingNativeFrameDataUrl = null;
    this.isNativeFrameImageLoading = true;

    let image: HTMLImageElement | null = this.nativeFrameImage;
    if (!image) {
      image = new Image();
      this.nativeFrameImage = image;
    }

    image.onload = (): void => {
      this.isNativeFrameImageLoading = false;
      this.drawFrame(image, image.naturalWidth || 480, image.naturalHeight || 640);
      this.loadPendingNativeFrame();
    };
    image.onerror = (): void => {
      this.isNativeFrameImageLoading = false;
      this.loadPendingNativeFrame();
    };
    image.src = dataUrl;
  }

  private drawFrame(source: CanvasImageSource, width: number, height: number): void {
    const canvasElement: HTMLCanvasElement | null = this.frameCanvasElement;
    const canvasContext: CanvasRenderingContext2D | null = this.frameCanvasContext;
    if (!canvasElement || !canvasContext) return;

    const safeWidth: number = Math.max(width, 1);
    const safeHeight: number = Math.max(height, 1);

    if (canvasElement.width !== safeWidth || canvasElement.height !== safeHeight) {
      canvasElement.width = safeWidth;
      canvasElement.height = safeHeight;
    }

    canvasContext.drawImage(source, 0, 0, safeWidth, safeHeight);
  }

  private queueSync(delayMs: number): void {
    if (!this.orderId) return;

    this.clearQueuedSync();

    if (delayMs <= 0) {
      this.syncForCurrentStateSafely();
      return;
    }

    this.queuedSyncTimeoutId = globalThis.setTimeout((): void => {
      this.queuedSyncTimeoutId = null;
      this.syncForCurrentStateSafely();
    }, delayMs);
  }

  private clearQueuedSync(): void {
    const timeoutId: ReturnType<typeof globalThis.setTimeout> | null = this.queuedSyncTimeoutId;
    this.queuedSyncTimeoutId = null;

    if (!timeoutId) return;

    globalThis.clearTimeout(timeoutId);
  }

  private syncForCurrentStateSafely(): void {
    void this.syncForCurrentState().catch((error: unknown): void => {
      console.error('Failed to sync iOS video call background camera state', error);
    });
  }

  private async syncForCurrentState(): Promise<void> {
    if (!this.orderId) return;

    if (!this.shouldUseNativeFrames()) {
      await this.logNativeCheckpoint('ios_background_camera_sync_foreground', {
        isUsingNativeFrames: this.isUsingNativeFrames,
      });
      this.isUsingNativeFrames = false;
      await this.deactivateNativeCamera();
      const restoredTrack: MediaStreamTrack | null = this.callbacks
        ? await this.callbacks.restoreForegroundVideoTrack()
        : null;
      if (restoredTrack) {
        await this.setForegroundVideoTrack(restoredTrack);
        await this.callbacks?.replaceSenderVideoTrack(restoredTrack);
      }
      this.startForegroundFramePump();
      return;
    }

    await this.logNativeCheckpoint('ios_background_camera_sync_background', {
      isUsingNativeFrames: this.isUsingNativeFrames,
    });
    const outgoingVideoTrack: MediaStreamTrack | null = this.outgoingVideoTrack;
    if (!outgoingVideoTrack || outgoingVideoTrack.readyState !== 'live') {
      await this.logNativeCheckpoint('ios_background_camera_sync_background_skipped', {
        hasOutgoingVideoTrack: !!outgoingVideoTrack,
        outgoingTrackReadyState: outgoingVideoTrack?.readyState ?? null,
      });
      return;
    }

    this.drawCurrentForegroundFrame();
    await this.callbacks?.replaceSenderVideoTrack(outgoingVideoTrack);
    this.isUsingNativeFrames = true;
    this.stopForegroundFramePump();
    this.callbacks?.releaseForegroundVideoTrack();
    await this.activateNativeCamera();
  }

  private shouldUseNativeFrames(): boolean {
    return !this.appStateChangeService.appIsActive || this.isDocumentHidden();
  }

  private isDocumentHidden(): boolean {
    if (this.platformService.isSSR) return false;
    if (typeof document === 'undefined') return false;
    return document.hidden;
  }

  private async activateNativeCamera(): Promise<void> {
    if (!this.orderId) return;

    const options: VideoCallBackgroundCameraOptions = {
      orderId: this.orderId,
      cameraFacingMode: this.currentFacingMode,
    };

    try {
      const status = await CallKit.activateVideoCallBackgroundCamera(options);
      await this.logNativeCheckpoint('ios_background_camera_native_activate_result', {
        supported: status.supported,
        active: status.active,
        reason: status.reason ?? null,
      });
      if (!status.supported || status.reason) {
        console.warn('Failed to activate iOS video call background camera', status);
      }
    } catch (error) {
      console.error('Failed to activate iOS video call background camera', error);
    }
  }

  private async deactivateNativeCamera(): Promise<void> {
    try {
      await CallKit.deactivateVideoCallBackgroundCamera();
    } catch (error) {
      console.error('Failed to deactivate iOS video call background camera', error);
    }
  }

  private async syncNativeCameraFacingMode(): Promise<void> {
    if (!this.orderId) return;

    try {
      await CallKit.updateVideoCallBackgroundCamera({
        orderId: this.orderId,
        cameraFacingMode: this.currentFacingMode,
      });
    } catch (error) {
      console.error('Failed to update iOS video call background camera facing mode', error);
    }
  }

  private async logNativeCheckpoint(
    step: string,
    data?: Record<string, string | number | boolean | null>,
  ): Promise<void> {
    try {
      await CallKit.debugLog({
        step,
        data,
      });
    } catch (error) {
      console.error('Failed to write native iOS background camera debug log', error);
    }
  }
}
